import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonBadge,
    IonModal,
    IonGrid,
    IonRow,
    IonCol,
    IonText,
    IonButton,
    useIonRouter
} from '@ionic/react';
import { useRemountOnPageHidden } from '../hooks/useRemountOnPageHidden';
import { list, calendarOutline, barChart, clipboard, checkmarkCircle, alertCircle, add, chevronForwardOutline, timeOutline, pricetagOutline, searchOutline } from 'ionicons/icons';
import styles from './Bookings.module.scss';
import BookingsListModal from '../components/BookingsListModal';
import BookingsAnalyticsModal from '../components/BookingsAnalyticsModal';
import Booking from './bookingSystem/BookingForm';
import BookingDetails from './bookingSystem/BookingDetails';
import SearchModal from '../components/SearchModal';
import ServiceTypeModal from '../components/ServiceTypeModal';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { ServiceInfo, ServiceType } from '@rsmaids/shared/types/services';
import { subscribeToUpcomingBookings } from '@rsmaids/shared/services/api/bookingsCacheService';
import { useTranslation } from 'react-i18next';

const Bookings: React.FC = () => {
  const { t } = useTranslation();
    const router = useIonRouter();
    const [showBookingsListModal, setShowBookingsListModal] = useState(false);
    const [bookingsListFilter, setBookingsListFilter] = useState<{ activeFilter: string; filterValue: string }>({ activeFilter: 'all', filterValue: 'all' });
    const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);
    const [showServicesModal, setShowServicesModal] = useState(false);
    const [serviceTypeOptions, setServiceTypeOptions] = useState<ServiceInfo[]>([]);
    const [serviceTypesLoading, setServiceTypesLoading] = useState(false);
    const [selectedServiceTypeForBooking, setSelectedServiceTypeForBooking] = useState<ServiceType | undefined>(undefined);
    const ionContentRef = useRef<HTMLIonContentElement | null>(null);
    const contentRef = useRef<HTMLIonGridElement>(null);
    const titleCardRef = useRef<HTMLDivElement>(null);
    const rafTick = useRef(false);
    const collapseDistance = 66;
    const [bookingStats, setBookingStats] = useState({
        total: 0,
        confirmed: 0,
        pending: 0,
        cancelled: 0,
        completionRate: 0
    });

    // Live Firestore subscription for hero card and badge — instant from cache, real-time updates
    useEffect(() => {
        const today = new Date();
        // Past bounded at 90 days, future effectively unbounded (10 years) —
        // matches onWixBookingWebhook.ts's isInWindow.
        const from = new Date(); from.setDate(today.getDate() - 90);
        const to = new Date(); to.setDate(today.getDate() + 3650);

        const unsub = subscribeToUpcomingBookings(from, to, (bookings) => {
            const realBookings = bookings.filter((b: BookingItem) =>
                !b.type || b.type === 'booking'
            );
            const confirmed = realBookings.filter((b: BookingItem) => b.status?.toLowerCase() === 'confirmed').length;
            const total = realBookings.length;
            // TODO: this should be based on bookings actually marked "Completed" via the
            // Complete button on the booking details page (BookingDetails.tsx), not
            // "confirmed" — confirmed includes future bookings that haven't happened yet.
            // That completion flow (dataService.bookings.updateBookingStatus) isn't fully
            // wired up/queryable yet, so this is a placeholder using "confirmed" as a stand-in
            // until real completed-status tracking exists.
            setBookingStats({
                total,
                confirmed,
                pending: realBookings.filter((b: BookingItem) => b.status?.toLowerCase() === 'pending').length,
                cancelled: realBookings.filter((b: BookingItem) => b.status?.toLowerCase() === 'cancelled' || b.status?.toLowerCase() === 'canceled').length,
                completionRate: total > 0 ? Math.round((confirmed / total) * 100) : 0,
            });
        });
        return unsub;
    }, []);

    // Listen for booking details modal trigger
    useEffect(() => {
        const removeListener = DataStorageService.setupStorageListener((key) => {
            if (key === 'open-booking-details-modal') {
                const bookingId = DataStorageService.getData<string>('open-booking-details-modal');
                if (bookingId) {
                    DataStorageService.removeData('open-booking-details-modal');
                    setSelectedBookingId(bookingId);
                    setShowBookingDetailsModal(true);
                }
            }
        });

        return removeListener;
    }, []);


    const handleCalendarNavigation = () => {
        router.push(import.meta.env.VITE_PLATFORM === 'desktop' ? '/tabs/schedule' : '/tabs/calendar');
    };

    const handleWorkScheduleNavigation = () => {
        router.push('/tabs/my-schedule');
    };

    // Opens the same "Select Service" browser used by the booking-creation
    // flow (ServiceTypeModal), here just for browsing — selecting an entry
    // simply closes the modal rather than starting a new booking.
    const handleOpenServices = async () => {
        setShowServicesModal(true);
        if (serviceTypeOptions.length > 0) return;
        setServiceTypesLoading(true);
        try {
            const dataService = DataServiceFactory.getService();
            const servicesData = await dataService.getServiceTypes();
            setServiceTypeOptions(servicesData);
        } catch (error) {
            console.error('Error fetching service types:', error);
        } finally {
            setServiceTypesLoading(false);
        }
    };

    // Opens the bookings list pre-filtered to a given status (or 'all') —
    // used by the Overview stat tiles so tapping "Confirmed"/"Pending"/etc.
    // jumps straight to that filtered view instead of the unfiltered list.
    const openBookingsListFiltered = (statusFilterValue: string) => {
        setBookingsListFilter(
            statusFilterValue === 'all'
                ? { activeFilter: 'all', filterValue: 'all' }
                : { activeFilter: 'booking-status', filterValue: statusFilterValue }
        );
        setShowBookingsListModal(true);
    };

    // Handle scroll for header and title card animation
    const applyProgress = useCallback((progress: number, smooth: boolean) => {
        const page = ionContentRef.current?.closest('.ion-page');
        const headerEl = page?.querySelector<HTMLElement>('[data-header-title]');

        if (smooth) {
            if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
            if (titleCardRef.current) {
                titleCardRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
            }
            setTimeout(() => {
                if (headerEl) headerEl.style.transition = '';
                if (titleCardRef.current) titleCardRef.current.style.transition = '';
            }, 220);
        }

        headerEl?.style.setProperty('--title-opacity', String(progress));

        if (titleCardRef.current) {
            titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
            titleCardRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
        }
    }, []);

    const handleScroll = useScrollSnap(contentRef, collapseDistance, applyProgress);

    // The list is rebuilt while the page is hidden, so it is back at the top on return
    const scrollResetKey = useRemountOnPageHidden(contentRef);
    // The header's collapsed/expanded look is driven by scroll events, and a rebuilt list at
    // scroll 0 fires none — so put the header back to expanded (title card shown, small title
    // hidden) explicitly, otherwise both show at once.
    useEffect(() => {
        applyProgress(0, false);
    }, [scrollResetKey, applyProgress]);

    return (
        <IonPage className={styles.page}>
            <BasePageWithHeader
                title={t('pages.Bookings.bookings')}
                toolbarClassName={styles.toolbar}
                smallTitleClassName={styles.smallTitle}
                titleHeaderClassName={styles.titleHeader}
                startButtons={
                    <IonButton shape='round' className={styles.searchButton} style={{ visibility: 'hidden' }}>
                        <IonIcon icon={searchOutline} />
                    </IonButton>
                }
                endButtons={
                    <IonButton shape='round' className={styles.searchButton} onClick={() => setShowSearchModal(true)}>
                        <IonIcon icon={searchOutline} />
                    </IonButton>
                }
            >
                <IonContent ref={ionContentRef} className={styles.content}>
                    <IonGrid
                        key={scrollResetKey}
                        ref={contentRef}
                        className={styles.bookingsContainer}
                        onScroll={(e) => {
                            const top = (e.target as HTMLElement).scrollTop || 0;
                            if (!rafTick.current) {
                                rafTick.current = true;
                                requestAnimationFrame(() => {
                                    handleScroll(top);
                                    rafTick.current = false;
                                });
                            }
                        }}
                    >
                        <div ref={titleCardRef} className={styles.titleCardWrapper}>
                            <TitleCard
                                title={t('pages.Bookings.bookings')}
                                subtitle={t('pages.Bookings.manageYourServiceBookingsAndSchedule')}
                                subtitleClassName={styles.heroSubtitle}
                                cardClassName={styles.hero}
                                titleContainerClassName={styles.titleContainer}
                                titleClassName={styles.heroTitle}
                            />
                        </div>

                        {/* Overview stats */}
                        <IonCard className={styles.sectionCard}>
                            <IonCardContent className={styles.section}>
                                <IonLabel className={styles.sectionTitle}>{t('pages.Bookings.overview')}</IonLabel>
                                <IonGrid className={styles.statsRow}>
                                    <IonRow>
                                        <IonCol className={styles.statCol} onClick={() => openBookingsListFiltered('all')}>
                                            <IonIcon icon={clipboard} className={styles.statIcon} />
                                            <IonText><p className={styles.statValue}>{bookingStats.total}</p></IonText>
                                            <IonText><p className={styles.statMeta}>{t('pages.Bookings.total')}</p></IonText>
                                        </IonCol>
                                        <IonCol className={styles.statCol} onClick={() => openBookingsListFiltered('confirmed')}>
                                            <IonIcon icon={checkmarkCircle} color="success" className={styles.statIcon} />
                                            <IonText><p className={styles.statValue}>{bookingStats.confirmed}</p></IonText>
                                            <IonText><p className={styles.statMeta}>{t('pages.Bookings.confirmed')}</p></IonText>
                                        </IonCol>
                                        <IonCol className={styles.statCol} onClick={() => openBookingsListFiltered('pending')}>
                                            <IonIcon icon={alertCircle} color="warning" className={styles.statIcon} />
                                            <IonText><p className={styles.statValue}>{bookingStats.pending}</p></IonText>
                                            <IonText><p className={styles.statMeta}>{t('pages.Bookings.pending')}</p></IonText>
                                        </IonCol>
                                        <IonCol className={`${styles.statCol} ${styles.statColLast}`} onClick={() => openBookingsListFiltered('confirmed')}>
                                            <IonIcon icon={barChart} color="success" className={styles.statIcon} />
                                            <IonText><p className={styles.statValue}>{bookingStats.completionRate}%</p></IonText>
                                            <IonText><p className={styles.statMeta}>{t('pages.Bookings.completion')}</p></IonText>
                                        </IonCol>
                                    </IonRow>
                                </IonGrid>
                            </IonCardContent>
                        </IonCard>

                        {/* Calendar View */}
                        <IonCard className={styles.sectionCard}>
                            <IonCardContent className={styles.section}>
                                <IonLabel className={styles.sectionTitle}>{t('pages.Bookings.calendarView')}</IonLabel>
                                <IonList lines="none" className={styles.navRows}>
                                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={handleCalendarNavigation}>
                                        <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                                            <IonIcon icon={calendarOutline} />
                                        </div>
                                        <IonLabel>
                                            <h3>{t('pages.Bookings.openCalendar')}</h3>
                                            <p>{t('pages.Bookings.navigateToCalendarView')}</p>
                                        </IonLabel>
                                    </IonItem>
                                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={handleWorkScheduleNavigation}>
                                        <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                                            <IonIcon icon={timeOutline} />
                                        </div>
                                        <IonLabel>
                                            <h3>{t('pages.Bookings.workSchedule')}</h3>
                                            <p>{t('pages.Bookings.viewStaffShiftsAndAvailability')}</p>
                                        </IonLabel>
                                    </IonItem>
                                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowBookingModal(true)}>
                                        <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                                            <IonIcon icon={add} />
                                        </div>
                                        <IonLabel>
                                            <h3>{t('pages.Bookings.addNewBooking')}</h3>
                                            <p>{t('pages.Bookings.createANewServiceBooking')}</p>
                                        </IonLabel>
                                    </IonItem>
                                </IonList>
                            </IonCardContent>
                        </IonCard>

                        {/* Bookings List */}
                        <IonCard className={styles.sectionCard}>
                            <IonCardContent className={styles.section}>
                                <IonLabel className={styles.sectionTitle}>{t('pages.Bookings.bookingsList')}</IonLabel>
                                <IonList lines="none" className={styles.navRows}>
                                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => openBookingsListFiltered('all')}>
                                        <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                                            <IonIcon icon={list} />
                                        </div>
                                        <IonLabel>
                                            <h3>{t('pages.Bookings.viewAllBookings')}</h3>
                                            <p>{t('pages.Bookings.searchFilterAndManageBookings')}</p>
                                        </IonLabel>
                                        <IonBadge slot="end" className={styles.navBadge}>{bookingStats.total}</IonBadge>
                                    </IonItem>
                                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={() => setShowAnalyticsModal(true)}>
                                        <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                                            <IonIcon icon={barChart} />
                                        </div>
                                        <IonLabel>
                                            <h3>{t('pages.Bookings.viewAnalytics')}</h3>
                                            <p>{t('pages.Bookings.revenueTrendsAndPerformanceMetrics')}</p>
                                        </IonLabel>
                                    </IonItem>
                                </IonList>
                            </IonCardContent>
                        </IonCard>

                        {/* Services */}
                        <IonCard className={styles.sectionCard}>
                            <IonCardContent className={styles.section}>
                                <IonLabel className={styles.sectionTitle}>{t('pages.Bookings.services')}</IonLabel>
                                <IonList lines="none" className={styles.navRows}>
                                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.navRow} onClick={handleOpenServices}>
                                        <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                                            <IonIcon icon={pricetagOutline} />
                                        </div>
                                        <IonLabel>
                                            <h3>{t('pages.Bookings.viewServices')}</h3>
                                            <p>{t('pages.Bookings.browseServiceTypesAndPricing')}</p>
                                        </IonLabel>
                                    </IonItem>
                                </IonList>
                            </IonCardContent>
                        </IonCard>
                    </IonGrid>
                </IonContent>

                {/* Modals */}
                <IonModal
                    isOpen={showBookingsListModal}
                    onDidDismiss={() => {
                        console.log('Bookings list modal dismissed');
                        setShowBookingsListModal(false);
                    }}
                    onWillDismiss={() => setShowBookingsListModal(false)}
                    canDismiss={true}
                    backdropDismiss={true}
                    keepContentsMounted={false}
                    handle={false}
                    className={styles.fullscreenModal}
                >
                    <BookingsListModal
                        onClose={() => setShowBookingsListModal(false)}
                        initialActiveFilter={bookingsListFilter.activeFilter}
                        initialFilterValue={bookingsListFilter.filterValue}
                    />
                </IonModal>

                <IonModal
                    isOpen={showAnalyticsModal}
                    onDidDismiss={() => {
                        console.log('Analytics modal dismissed');
                        setShowAnalyticsModal(false);
                    }}
                    onWillDismiss={() => setShowAnalyticsModal(false)}
                    canDismiss={true}
                    backdropDismiss={true}
                    keepContentsMounted={false}
                    handle={false}
                    className={styles.fullscreenModal}
                >
                    <BookingsAnalyticsModal onClose={() => setShowAnalyticsModal(false)} />
                </IonModal>

                <Booking
                    isOpen={showBookingModal}
                    onClose={() => {
                        setShowBookingModal(false);
                        setSelectedServiceTypeForBooking(undefined);
                    }}
                    initialServiceType={selectedServiceTypeForBooking}
                />

                <SearchModal
                    isOpen={showSearchModal}
                    onClose={() => setShowSearchModal(false)}

                />

                {showBookingDetailsModal && selectedBookingId && (
                    <BookingDetails
                        bookingId={selectedBookingId}
                        isOpen={showBookingDetailsModal}
                        onClose={() => {
                            setShowBookingDetailsModal(false);
                            setSelectedBookingId(undefined);
                        }}
                    />
                )}

                <ServiceTypeModal
                    isOpen={showServicesModal}
                    onClose={() => setShowServicesModal(false)}
                    onSelectServiceType={(type) => {
                        setShowServicesModal(false);
                        setSelectedServiceTypeForBooking(type);
                        setShowBookingModal(true);
                    }}
                    selectedServiceType=""
                    services={serviceTypeOptions}
                    loading={serviceTypesLoading}
                />
            </BasePageWithHeader>
        </IonPage>
    );
};

export default Bookings;